import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { T, Icon } from "./businessTheme";

/* ── Field ── */
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: T.n700, display: "block", marginBottom: 7 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "11px 14px", borderRadius: 10,
  border: `1.5px solid ${T.n200}`, fontSize: 13.5, fontFamily: T.sans,
  outline: "none", boxSizing: "border-box", color: T.n900, background: T.white,
};

/* ── Toggle switch ── */
function Toggle({ checked, onChange }) {
  return (
    <button onClick={() => onChange(!checked)} style={{
      width: 42, height: 24, borderRadius: 99, border: "none", cursor: "pointer",
      background: checked ? T.g500 : T.n200, position: "relative", flexShrink: 0,
      transition: "background .18s",
    }}>
      <span style={{
        position: "absolute", top: 3, left: checked ? 21 : 3,
        width: 18, height: 18, borderRadius: "50%", background: T.white,
        transition: "left .18s", boxShadow: "0 1px 3px rgba(0,0,0,.2)",
      }} />
    </button>
  );
}

/* ── Section header inside drawer ── */
function DrawerSection({ icon, title, children }) {
  return (
    <div style={{ marginBottom: 30 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 16 }}>
        <Icon name={icon} size={15} color={T.g600} strokeWidth={2} />
        <h3 style={{ fontSize: 13, fontWeight: 700, color: T.n900, letterSpacing: "0.03em", textTransform: "uppercase", margin: 0 }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

/* ── Row toggle item ── */
function ToggleRow({ label, sub, checked, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${T.n100}`, gap: 16 }}>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n800 }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: T.n400, marginTop: 2 }}>{sub}</div>}
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

/* ── Save status indicator ── */
function SaveStatus({ status }) {
  if (!status) return null;
  const isError = status === "error";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 600, color: isError ? "#dc2626" : T.g600 }}>
      <Icon name={isError ? "AlertCircle" : "CheckCircle"} size={14} color={isError ? "#dc2626" : T.g600} strokeWidth={2} />
      {isError ? "Failed to save. Try again." : "Saved"}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function SettingsDrawer({ open, onClose, account, onUpdateAccount }) {
  const [tab, setTab] = useState("account");
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null | "saved" | "error"

  // ── Controlled form state for Account tab ────────────────────────────────
  const [formValues, setFormValues] = useState({
    name:  account?.name  ?? "",
    email: account?.email ?? "",
    phone: account?.phone ?? "",
    city:  account?.city  ?? "",
  });

  // ── Controlled form state for Billing prefs tab ──────────────────────────
  const [billingEmail, setBillingEmail] = useState(account?.email ?? "");
  const [invoiceNotes, setInvoiceNotes] = useState("");

  // ── Notification toggles ─────────────────────────────────────────────────
  const [notifs, setNotifs] = useState({
    newBooking:    true,
    jobComplete:   true,
    invoiceReady:  true,
    proAssigned:   true,
    disputeUpdate: true,
    weeklyDigest:  false,
  });

  // Re-sync form if account prop changes (e.g. after an external update)
  useEffect(() => {
    if (account) {
      setFormValues({
        name:  account.name  ?? "",
        email: account.email ?? "",
        phone: account.phone ?? "",
        city:  account.city  ?? "",
      });
      setBillingEmail(account.email ?? "");
    }
  }, [account?.id]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Clear save status when switching tabs
  useEffect(() => { setSaveStatus(null); }, [tab]);

  // ── Save handler ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Notification and security tabs have no Supabase writes yet —
    // just close the drawer.
    if (tab === "notifications" || tab === "security") {
      onClose();
      return;
    }

    if (!account?.id) {
      // No real account row yet (dev/mock mode) — close silently
      onClose();
      return;
    }

    setSaving(true);
    setSaveStatus(null);

    try {
      let updatePayload = {};

      if (tab === "account") {
        updatePayload = {
          name:  formValues.name.trim()  || account.name,
          email: formValues.email.trim() || account.email,
          phone: formValues.phone.trim(),
          city:  formValues.city.trim(),
        };
      }

      if (tab === "billing") {
        updatePayload = {
          email: billingEmail.trim() || account.email,
        };
      }

      const { data, error } = await supabase
        .from("business_accounts")
        .update(updatePayload)
        .eq("id", account.id)
        .select()
        .single();

      if (error) throw error;

      // Push updated account back up to BusinessDashboard context
      if (onUpdateAccount && data) onUpdateAccount(data);

      setSaveStatus("saved");
      // Auto-close after a short confirmation pause
      setTimeout(() => {
        setSaveStatus(null);
        onClose();
      }, 1200);

    } catch (err) {
      console.error("[SettingsDrawer] Save failed:", err);
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const setField = (key) => (e) =>
    setFormValues(prev => ({ ...prev, [key]: e.target.value }));

  // ─────────────────────────────────────────────────────────────────────────

  const TABS = [
    { id: "account",       label: "Account",       icon: "Building2" },
    { id: "notifications", label: "Notifications", icon: "Bell"      },
    { id: "security",      label: "Security",      icon: "Shield"    },
    { id: "billing",       label: "Billing prefs", icon: "Wallet"    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 998,
          background: "rgba(15,17,23,.45)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .25s ease",
        }}
      />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 999,
        width: "min(540px, 100vw)", background: T.white,
        boxShadow: "-8px 0 40px rgba(0,0,0,.18)",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform .3s cubic-bezier(.4,0,.2,1)",
        display: "flex", flexDirection: "column",
      }}>

        {/* Header */}
        <div style={{ padding: "22px 26px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <h2 style={{ fontFamily: T.serif, fontSize: 24, fontWeight: 400, color: T.n900, margin: 0, letterSpacing: "-0.02em" }}>Settings</h2>
            <p style={{ fontSize: 12.5, color: T.n400, margin: "3px 0 0" }}>{account?.name}</p>
          </div>
          <button onClick={onClose} style={{
            width: 34, height: 34, borderRadius: "50%", background: T.n50, border: `1px solid ${T.n150}`,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="X" size={15} color={T.n600} strokeWidth={2.2} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, padding: "14px 26px 0", borderBottom: `1px solid ${T.n100}`, flexShrink: 0, overflowX: "auto" }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "9px 14px", border: "none", background: "none", cursor: "pointer",
              fontFamily: T.sans, fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap",
              color: tab === t.id ? T.g600 : T.n400,
              borderBottom: `2px solid ${tab === t.id ? T.g500 : "transparent"}`,
              marginBottom: -1,
            }}>
              <Icon name={t.icon} size={13} strokeWidth={2} color={tab === t.id ? T.g600 : T.n400} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 26px" }}>

          {/* ── Account tab ── */}
          {tab === "account" && (
            <>
              <DrawerSection icon="Building2" title="Business profile">
                <Field label="Business name">
                  <input
                    style={inputStyle}
                    value={formValues.name}
                    onChange={setField("name")}
                  />
                </Field>
                <Field label="Contact email">
                  <input
                    style={inputStyle}
                    type="email"
                    value={formValues.email}
                    onChange={setField("email")}
                  />
                </Field>
                <Field label="Contact phone">
                  <input
                    style={inputStyle}
                    type="tel"
                    value={formValues.phone}
                    onChange={setField("phone")}
                  />
                </Field>
                <Field label="City">
                  <input
                    style={inputStyle}
                    value={formValues.city}
                    onChange={setField("city")}
                  />
                </Field>
              </DrawerSection>

              <DrawerSection icon="ArrowLeftRight" title="Linked accounts">
                <div style={{ padding: "14px 16px", borderRadius: 12, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", gap: 12 }}>
                  <Icon name="CheckCircle" size={16} color={T.g600} strokeWidth={2} />
                  <p style={{ fontSize: 12.5, color: T.g700, margin: 0, lineHeight: 1.6 }}>
                    This business account is linked to your personal JiraniApp account. Use the switcher in the top bar to move between them.
                  </p>
                </div>
              </DrawerSection>
            </>
          )}

          {/* ── Notifications tab ── */}
          {tab === "notifications" && (
            <DrawerSection icon="Bell" title="Email and push notifications">
              <ToggleRow label="New booking confirmed"  sub="When a Pro accepts a booking"                checked={notifs.newBooking}    onChange={v => setNotifs(s => ({ ...s, newBooking: v }))} />
              <ToggleRow label="Job marked complete"    sub="When a Pro finishes a job"                   checked={notifs.jobComplete}   onChange={v => setNotifs(s => ({ ...s, jobComplete: v }))} />
              <ToggleRow label="Invoice ready"          sub="When your monthly invoice is generated"      checked={notifs.invoiceReady}  onChange={v => setNotifs(s => ({ ...s, invoiceReady: v }))} />
              <ToggleRow label="Reserved Pro assigned"  sub="When a new Pro joins your team"              checked={notifs.proAssigned}   onChange={v => setNotifs(s => ({ ...s, proAssigned: v }))} />
              <ToggleRow label="Dispute updates"        sub="Status changes on open disputes"             checked={notifs.disputeUpdate} onChange={v => setNotifs(s => ({ ...s, disputeUpdate: v }))} />
              <ToggleRow label="Weekly digest"          sub="Summary of bookings and spend every Monday"  checked={notifs.weeklyDigest}  onChange={v => setNotifs(s => ({ ...s, weeklyDigest: v }))} />
            </DrawerSection>
          )}

          {/* ── Security tab ── */}
          {tab === "security" && (
            <>
              <DrawerSection icon="Shield" title="Login and access">
                <div style={{ padding: "16px", borderRadius: 12, border: `1px solid ${T.n150}`, marginBottom: 14 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n800, marginBottom: 4 }}>Two-factor authentication</div>
                  <p style={{ fontSize: 12, color: T.n400, margin: "0 0 12px", lineHeight: 1.6 }}>Add an extra layer of security to your business account.</p>
                  <button style={{ padding: "8px 16px", borderRadius: 8, border: `1.5px solid ${T.g500}`, background: "transparent", color: T.g600, fontSize: 12.5, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
                    Enable 2FA
                  </button>
                </div>
                <button style={{ width: "100%", padding: "12px", borderRadius: 10, border: `1.5px solid ${T.n200}`, background: "transparent", color: T.n700, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer", textAlign: "left" }}>
                  Change password
                </button>
              </DrawerSection>

              <DrawerSection icon="LogOut" title="Session">
                <button style={{ width: "100%", padding: "12px", borderRadius: 10, border: `1.5px solid ${T.redBorder}`, background: T.redBg, color: T.red, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <Icon name="LogOut" size={14} color={T.red} strokeWidth={2} />
                  Sign out of this device
                </button>
              </DrawerSection>
            </>
          )}

          {/* ── Billing prefs tab ── */}
          {tab === "billing" && (
            <DrawerSection icon="Wallet" title="Default invoice settings">
              <Field label="Billing email">
                <input
                  style={inputStyle}
                  type="email"
                  value={billingEmail}
                  onChange={e => setBillingEmail(e.target.value)}
                />
              </Field>
              <Field label="Invoice notes (shown on every invoice)">
                <textarea
                  style={{ ...inputStyle, minHeight: 80, resize: "vertical", fontFamily: T.sans }}
                  placeholder="Optional notes, PO numbers, etc."
                  value={invoiceNotes}
                  onChange={e => setInvoiceNotes(e.target.value)}
                />
              </Field>
              <p style={{ fontSize: 12, color: T.n400, lineHeight: 1.6, margin: "4px 0 0" }}>
                To change your payment method (M-Pesa or bank transfer), use the Billing section in the main dashboard.
              </p>
            </DrawerSection>
          )}

        </div>

        {/* Footer */}
        <div style={{
          padding: "18px 26px", borderTop: `1px solid ${T.n100}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, flexShrink: 0,
        }}>
          {/* Left: save status feedback */}
          <SaveStatus status={saveStatus} />

          {/* Right: action buttons */}
          <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: "11px 20px", borderRadius: 10, border: `1.5px solid ${T.n200}`,
                background: "transparent", color: T.n700, fontSize: 13, fontWeight: 600,
                fontFamily: T.sans, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.5 : 1,
              }}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "11px 24px", borderRadius: 10, border: "none",
                background: saving ? T.g300 : T.g500, color: T.white,
                fontSize: 13, fontWeight: 600, fontFamily: T.sans,
                cursor: saving ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 8,
                transition: "background .15s",
              }}
              onMouseEnter={e => { if (!saving) e.currentTarget.style.background = T.g600; }}
              onMouseLeave={e => { if (!saving) e.currentTarget.style.background = T.g500; }}>
              {saving && (
                <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite" }} />
              )}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </>
  );
}